import React, { useEffect, useState } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Quote, Users, EyeOff, Globe, Pencil, Trash2, X, Check, MessageSquareQuote } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PROMPT = "So far, what's your favorite part of Sovereign Signal?";
const HELPER = "A sentence or two. A moment, a feeling, or something you can do here.";

const VIS = {
  members: { label: "Members", icon: Users, note: "Shown on the wall to fellow sovereigns." },
  anonymous: { label: "Anonymous", icon: EyeOff, note: "Shown as \u201cA Sovereign.\u201d We may quote it, never with your name." },
  public: { label: "Public", icon: Globe, note: "Shown with your name, and may appear on our public Compass." },
};

const VisBadge = ({ visibility }) => {
  const v = VIS[visibility] || VIS.members;
  const Icon = v.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500" data-testid={`voice-badge-${visibility}`}>
      <Icon size={11} /> {v.label}
    </span>
  );
};

// The gentle nudge shown on the feed at a member's 3rd visit.
export const VoicesInviteCard = () => {
  const { user, dismissTip } = useAuth();
  const navigate = useNavigate();
  const dismissedKey = "voices-invite@1";
  const dismissed = (user?.dismissed_tips || []).includes(dismissedKey);
  if (!user || (user.visit_count || 0) < 3 || dismissed) return null;

  const go = () => { dismissTip("voices-invite", 1); navigate("/voices"); };
  return (
    <div className="relative mb-6 rounded-xl bg-white border-l-4 border-[#14A89A] shadow-xl shadow-black/50 ring-1 ring-black/5 p-4 pr-11 fade-up" data-testid="voices-invite-card">
      <button onClick={() => dismissTip("voices-invite", 1)} aria-label="Dismiss" data-testid="voices-invite-dismiss" className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-colors">
        <X size={16} strokeWidth={2.25} />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-[#14A89A]/10 text-[#14A89A]"><MessageSquareQuote size={18} strokeWidth={2} /></div>
        <div className="min-w-0 flex-1">
          <div className="font-body text-[10px] font-semibold tracking-[0.22em] uppercase text-[#14A89A] mb-1">Sovereign Voices</div>
          <div className="font-heading text-sm text-zinc-900 mb-1">{PROMPT}</div>
          <div className="font-body text-xs text-zinc-600 leading-relaxed">Share a line or a story on the Voices wall. It helps new sovereigns feel what this place is.</div>
          <button onClick={go} data-testid="voices-invite-go" className="mt-3 inline-flex items-center font-body text-[11px] tracking-[0.15em] uppercase font-semibold text-[#0A0A0A] bg-[#14A89A] hover:bg-[#2BC4B5] px-3 py-1.5 rounded-md transition-colors">Add your voice</button>
        </div>
      </div>
    </div>
  );
};

export default function SovereignVoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [voices, setVoices] = useState(null);
  const [quote, setQuote] = useState("");
  const [format, setFormat] = useState("quote");
  const [visibility, setVisibility] = useState("members");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/voices").then(({ data }) => setVoices(data)).catch(() => setVoices([]));
  useEffect(() => { load(); }, []);

  const reset = () => { setQuote(""); setFormat("quote"); setVisibility("members"); setEditingId(null); setErr(""); };

  const submit = async () => {
    if (!quote.trim()) return;
    setSaving(true); setErr("");
    const payload = { quote: quote.trim(), format, visibility, display_name: user?.name };
    try {
      if (editingId) await api.patch(`/voices/${editingId}`, payload);
      else await api.post("/voices", payload);
      reset(); await load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not save. Please try again."); }
    setSaving(false);
  };

  const startEdit = (v) => { setEditingId(v.id); setQuote(v.quote); setFormat(v.format); setVisibility(v.visibility); setErr(""); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => {
    if (!window.confirm("Remove this voice?")) return;
    const prev = voices;
    setVoices((vs) => vs.filter((v) => v.id !== id));
    try { await api.delete(`/voices/${id}`); } catch { setVoices(prev); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8" data-testid="sovereign-voices-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ArrowLeft size={14} /> Back</button>

      <header className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Quote size={18} className="text-[#14A89A]" />
          <h1 className="font-heading text-3xl font-medium tracking-tight text-[#14A89A]">Sovereign Voices</h1>
        </div>
        <p className="font-body text-sm text-zinc-500 leading-relaxed">Why we're here, in our own words. Share a quote or a short story, and choose who gets to see it. This is our one on-the-record space, so only what you share here is ever readable.</p>
      </header>

      {/* Composer */}
      <div className="border border-[#14A89A]/30 bg-[#14A89A]/[0.05] p-5 mb-10" data-testid="voice-composer">
        <div className="font-heading text-sm text-white mb-1">{PROMPT}</div>
        <p className="font-body text-xs text-zinc-500 mb-3">{HELPER}</p>
        <textarea
          value={quote} onChange={(e) => setQuote(e.target.value)} maxLength={1000} rows={4}
          data-testid="voice-input" placeholder="Speak freely..."
          className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body leading-relaxed resize-none"
        />

        {/* Format */}
        <div className="flex items-center gap-2 mt-3">
          {["quote", "story"].map((f) => (
            <button key={f} onClick={() => setFormat(f)} data-testid={`voice-format-${f}`} className={`px-3 py-1.5 font-body text-xs tracking-wide uppercase border transition-colors ${format === f ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-500 hover:text-white"}`}>{f}</button>
          ))}
        </div>

        {/* Visibility */}
        <div className="mt-4 space-y-2">
          <p className="font-body text-[10px] uppercase tracking-wider text-zinc-500">Who can see it</p>
          {Object.entries(VIS).map(([key, v]) => {
            const Icon = v.icon;
            return (
              <button key={key} onClick={() => setVisibility(key)} data-testid={`voice-visibility-${key}`} className={`w-full flex items-start gap-3 text-left border px-3 py-2.5 transition-colors ${visibility === key ? "border-[#14A89A] bg-[#14A89A]/10" : "border-white/12 hover:border-white/25"}`}>
                <Icon size={15} className={`shrink-0 mt-0.5 ${visibility === key ? "text-[#14A89A]" : "text-zinc-500"}`} />
                <div className="min-w-0">
                  <div className={`font-body text-sm ${visibility === key ? "text-white" : "text-zinc-300"}`}>{v.label}</div>
                  <div className="font-body text-[11px] text-zinc-500 leading-snug">{v.note}</div>
                </div>
              </button>
            );
          })}
        </div>

        {err && <p className="font-body text-[11px] text-red-400 mt-3" data-testid="voice-error">{err}</p>}
        <div className="flex items-center gap-2 mt-4">
          <button onClick={submit} disabled={saving || !quote.trim()} data-testid="voice-submit" className="inline-flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-2.5 text-sm font-body hover:bg-[#2BC4B5] transition-colors disabled:opacity-40">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} {editingId ? "Update" : "Share"}
          </button>
          {editingId && <button onClick={reset} data-testid="voice-cancel-edit" className="font-body text-xs text-zinc-500 hover:text-white transition-colors">Cancel</button>}
        </div>
      </div>

      {/* Wall */}
      <div className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase mb-4">The Voices</div>
      {voices === null ? (
        <div className="flex justify-center py-16"><SignalPulse size={44} /></div>
      ) : voices.length === 0 ? (
        <p className="font-body text-sm text-zinc-600 py-10" data-testid="voices-empty">No voices yet. Be the first to speak.</p>
      ) : (
        <div className="space-y-4" data-testid="voices-wall">
          {voices.map((v) => (
            <div key={v.id} className={`border p-4 ${v.mine ? "border-[#14A89A]/30 bg-[#14A89A]/[0.04]" : "border-white/10"}`} data-testid={`voice-${v.id}`}>
              <p className="font-body text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{v.format === "quote" ? `\u201c${v.quote}\u201d` : v.quote}</p>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-xs text-[#14A89A]">{v.name}</span>
                  {v.mine && <span className="text-zinc-600">·</span>}
                  {v.mine && <VisBadge visibility={v.visibility} />}
                </div>
                {v.mine && (
                  <div className="flex items-center gap-3">
                    <button onClick={() => startEdit(v)} data-testid={`voice-edit-${v.id}`} className="text-zinc-500 hover:text-[#14A89A] transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => remove(v.id)} data-testid={`voice-delete-${v.id}`} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
